import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareIcon from '@mui/icons-material/Share';
import {
  asArray,
  BRAND_BLUE,
  EmptyState,
  ErrorState,
  formatNumber,
  hasMeaningfulValue,
  LoadingState,
  OsintShell,
  pick,
  SectionCard,
  SUCCESS_GREEN,
  useOsintFetch,
} from './osintCommon';
import {
  CompanyOverview,
  dedupeLocations,
  HeadquartersLocationSection,
  ProductCards,
  RelationshipCards,
  SocialSummaryProfiles,
} from './osintRenderers';

const countLocations = (company) => {
  const locations = asArray(pick(company, ['locations', 'offices'], []));
  const socialLocations = Object.values(pick(company, ['social_media', 'socialMedia', 'socials'], {}))
    .flatMap((platform) => {
      const profile = platform?.profile || platform || {};
      const basic = profile.basic_info || profile.basicInfo || profile;
      const locationData = basic.locations || profile.locations || {};
      return [
        locationData.headquarters,
        ...asArray(locationData.offices),
      ];
    })
    .filter(hasMeaningfulValue);
  return dedupeLocations([...locations, ...socialLocations]).length;
};

const mergeLists = (...lists) => {
  const seen = new Set();
  return lists
    .flatMap((list) => asArray(list))
    .filter(hasMeaningfulValue)
    .filter((item) => {
      const key = typeof item === 'object'
        ? JSON.stringify({
          name: item.name || item.title || item.company || item.url || '',
          description: item.description || item.summary || '',
        }).toLowerCase()
        : String(item).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const CompanyPage = () => {
  const { pentest_id } = useParams();
  const companyFetch = useOsintFetch('/pentests/osint/company', pentest_id, 'company');
  const productsFetch = useOsintFetch('/pentests/osint/products', pentest_id, 'products');
  const relationshipsFetch = useOsintFetch('/pentests/osint/relationships', pentest_id, 'relationships');
  const { loading, error, data: company } = companyFetch;

  if (loading || productsFetch.loading || relationshipsFetch.loading) return <LoadingState label="Loading company intelligence..." />;
  if (error) return <ErrorState message={error} />;
  if (!company || !Object.keys(company).length) {
    return (
      <OsintShell>
        <EmptyState title="No company profile returned" description="The OSINT agent did not return company profile data for this scan yet." />
      </OsintShell>
    );
  }

  const industry = pick(company, ['industry', 'sector'], '');
  const employeeCount = pick(company, ['employee_count', 'employees_count', 'employees'], '');
  const founded = pick(company, ['founded_year', 'founded', 'foundedYear'], '');
  const social = pick(company, ['social_media', 'socialMedia', 'socials'], {});
  const socialEntries = Object.entries(social || {}).filter(([, value]) => hasMeaningfulValue(value));

  const endpointProducts = productsFetch.data || {};
  const endpointRelationships = relationshipsFetch.data || {};
  const products = {
    saas_products: mergeLists(pick(endpointProducts, ['saas_products', 'saasProducts'], []), pick(company, ['saas_products', 'saasProducts'], [])),
    mobile_apps: mergeLists(pick(endpointProducts, ['mobile_apps', 'mobileApps'], []), pick(company, ['mobile_apps', 'mobileApps'], [])),
    physical_products: mergeLists(pick(endpointProducts, ['physical_products', 'physicalProducts'], []), pick(company, ['physical_products', 'physicalProducts'], [])),
    services: mergeLists(
      pick(endpointProducts, ['services'], []),
      pick(company, ['services'], []),
      pick(company, ['products_services', 'productsServices'], []),
    ),
  };

  const relationships = {
    customers: mergeLists(pick(endpointRelationships, ['customers'], []), pick(company, ['customers'], [])),
    partners: mergeLists(pick(endpointRelationships, ['partners'], []), pick(company, ['partners'], [])),
    other_relationships: mergeLists(
      pick(endpointRelationships, ['other_relationships', 'otherRelationships', 'relationships'], []),
      pick(company, ['relationships'], []),
      pick(company, ['other_relationships', 'otherRelationships'], []),
    ),
  };

  const metrics = [
    { label: 'Employees', value: hasMeaningfulValue(employeeCount) ? formatNumber(employeeCount) : 'Unknown', helper: 'Publicly inferred organization size or employee count.', Icon: GroupsIcon, accent: BRAND_BLUE },
    { label: 'Founded', value: founded || 'Unknown', helper: 'Founding year or date discovered from company sources.', Icon: BusinessIcon, accent: SUCCESS_GREEN },
    { label: 'Locations', value: formatNumber(countLocations(company)), helper: 'Known offices, headquarters, and public location references.', Icon: LocationOnIcon, accent: '#7C3AED' },
    { label: 'Social Links', value: formatNumber(socialEntries.length), helper: 'Company social platforms and public profile references.', Icon: ShareIcon, accent: '#0EA5E9' },
    { label: 'Industry', value: industry || 'Unknown', helper: 'Primary market or sector identified from public company sources.', Icon: BusinessIcon, accent: '#2563EB' },
  ];

  return (
    <OsintShell>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CompanyOverview company={company} metrics={metrics} />
        </Grid>

        <Grid item xs={12}>
          <HeadquartersLocationSection company={company} />
        </Grid>

        <Grid item xs={12}>
          <ProductCards products={products} />
        </Grid>

        <Grid item xs={12}>
          <RelationshipCards relationships={relationships} />
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            eyebrow="Social Presence"
            title="Company social media"
            description="Concise public profile references. Use Media & Social Intelligence for the deeper social activity view."
          >
            <SocialSummaryProfiles social={social} />
          </SectionCard>
        </Grid>
      </Grid>
    </OsintShell>
  );
};

export default CompanyPage;
